import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertCircle,
  AlertTriangle,
  ArrowLeft,
  CalendarPlus,
  ChevronDown,
  ClipboardList,
  FileArchive,
  FileText,
  Pencil,
  Plus,
  StickyNote,
  Trash2,
  Target,
} from 'lucide-react';
import { EvvReadingsDialog } from '../components/visit/EvvReadingsDialog';
import VisitTaskPicker from '../components/visit/VisitTaskPicker';
import PatientAllergies from '../components/PatientAllergies';
import { outstandingLines } from '../components/imported/importedChartLines';
import { EventsTab } from '../components/patient/EventsTab';
import { AuthorizationsTab } from '../components/patient/AuthorizationsTab';
import { InsuranceTab } from '../components/patient/InsuranceTab';
import { TasksTab } from '../components/patient/TasksTab';
import { AddTaskDialog } from '../components/patient/AddTaskDialog';
import GoalsTab from '../components/goals/GoalsTab';
import AddExternalEmrGoalsDialog from '../components/goals/AddExternalEmrGoalsDialog';
import { PatientWounds } from '../components/wounds';
import { ExternallyBilledBadge } from '../components/episode/ExternallyBilledBadge';
import {
  getPatient,
  updateOrder,
  getAvailableClinicians,
  getPatientWounds,
  getPatientDocuments,
  getIntakes,
  updateVisit,
  getPatientAllergies,
  getPatientMedications,
  getImportedAllergyProfile,
  getImportedMedicationLines,
  listPatientCommunications,
  updatePatientCommunication,
  updateMiscTask,
  listAgencyClinicPhysicians,
  batchPrintEpisodeTasks,
  listPatientInsurances,
  updateEpisodeAssignments,
} from '../services/api';
import { useStore } from '../store';
import PatientDocumentsTab from '../components/documents/PatientDocumentsTab';
import ScheduleVisitsDialog from '../components/ScheduleVisitsDialog';
import type { PatientDocumentsTabHandle } from '../components/documents/PatientDocumentsTab';
import { PatientMedications } from '../components/medications';
import { countActiveMedications } from '../components/medications/medicationUtils';
import type {
  PatientResponse,
  PatientInsuranceResponse,
  TaskListResponse,
  AvailableClinicianResponse as AvailableClinician,
  ClinicPhysicianResponse,
  PatientCommunicationResponse,
  OrderResponse,
  VisitTypeId,
} from '../services/api/types.gen';
import { AUTHORIZATION_ERROR_CODE } from '../utils/authorizationBudget';
import { useVisitTypeOptions } from '../hooks/useVisitTypeOptions';
import { useToast } from '../hooks/useToastContext';
import { usePrintView } from '../hooks/usePrintView';
import { usePdfExport } from '../hooks/usePdfExport';
import { useActiveGoalCount } from '../hooks/useActiveGoalCount';
import { useAuthorizationBudget } from '../hooks/useAuthorizationBudget';
import { useIsMobile } from '../hooks/useIsMobile';
import {
  usePatientDetailEpisodes,
  useAddTaskTargetEpisode,
} from '../hooks/usePatientDetailEpisodes';
import { usePatientDetailTasks } from '../hooks/usePatientDetailTasks';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  formatDateToMMDDYYYY,
  formatDateToISO,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import {
  clinicianMatchesDiscipline,
  getAssigneeDisciplineForTask,
  getClinicianDisplayName as formatClinicianName,
  type ClinicianSelectInput,
} from '../utils/clinicianUtils';
import {
  getBusinessErrorMessage,
  getConflictCode,
} from '../utils/apiErrorDetail';
import { useCanViewSchedulingPointLoads } from '../hooks/useCanViewSchedulingPointLoads';
import { useInvalidateSchedulingPointLoads } from '../hooks/useSchedulingPointLoads';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { PatientHeader } from '../components/patient/PatientHeader';
import { PatientInfoTab } from '../components/patient/PatientInfoTab';
import { PatientFaceSheet } from '../components/patient/PatientFaceSheet';
import AdmissionTab from '../components/patient/AdmissionTab';
import { PatientInsuranceFormDialog } from '../components/patient/PatientInsuranceFormDialog';
import { ThirtyDaySummaryDetailPanel } from '../components/patient/ThirtyDaySummaryDetailPanel';
import { THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE } from '../components/patient/patientCommunicationTypes';
import { CommunicationDetailPanel } from '../components/patient/CommunicationDetailPanel';
import { MiscTaskDetailPanel } from '../components/patient/MiscTaskDetailPanel';
import EpisodeEditor from '../components/episode/EpisodeEditor';
import EpisodeDateSyncDialog from '../components/episode/EpisodeDateSyncDialog';
import {
  boundaryRescheduleBounds,
  type EpisodeBoundary,
} from '../utils/episodeBoundaryVisits';
import { OASIS_VISIT_TYPE_LABELS } from '../constants/oasisVisitTypes';
import { getHttpStatus } from '../utils/httpErrorUtils';
import { EpisodeInsuranceSelector } from '../components/episode/EpisodeInsuranceSelector';
import { EpisodeWorksheetEditor } from '../components/episode/EpisodeWorksheetEditor';
import { VisitDetailPanel } from '../components/visit/VisitDetailPanel';
import { OrderDetailPanel } from '../components/order/OrderDetailPanel';
import AddEpisodeModal from '../components/AddEpisodeModal';
import type {
  ConfirmSpec,
  SelectionAction,
} from '../components/SelectionActionBar';
import BatchNotesDialog from '../components/task/BatchNotesDialog';
import {
  deleteConflictCode,
  deleteConflictCopy,
  deleteConflictSentence,
  deleteTaskRequest,
  partitionDeletable,
  partitionNotStarted,
  partitionPrintable,
  buildPrintConfirm,
  toBatchPrintTaskRefs,
} from '../components/task/batchActions';
import {
  TASK_TYPE_DISCIPLINE_MAP,
  showsGoalsSection,
  supportsWounds,
  type TaskTypeId,
} from '../constants/visitTypes';
import { visitGoalCapabilities } from '../utils/goalTaskCapabilities';
import { PermissionSlug } from '../constants/featureFlags';
import { isVisitTaskRow } from '../components/task/taskTypeFilters';
import { RestoreTaskButton } from '../components/task/RestoreTaskButton';
import { BillingManagerLink } from '../components/billing/BillingManagerLink';
import { UnclaimedClinicianName } from '../components/clinician/UnclaimedClinicianName';
import {
  activeEpisodeBoundaryForVisit,
  episodeBlocksNewVisits,
} from '../components/patient/patientDetailTaskConstants';

const LoadingSpinner: React.FC<{ size?: 'sm' | 'md'; text?: string }> = ({
  size = 'md',
  text,
}) => {
  const sizeClass = size === 'sm' ? 'h-3 w-3' : 'h-8 w-8';
  return (
    <span className='flex items-center gap-1'>
      <div
        className={`animate-spin rounded-full border-b-2 border-primary ${sizeClass}`}
      />
      {text && <span>{text}</span>}
    </span>
  );
};

const PATIENT_TABS = [
  'visits',
  'documents',
  'medications',
  'allergies',
  'wounds',
  'goals',
  'insurance',
  'authorizations',
  'events',
  'info',
  'admission',
] as const;
type PatientTab = (typeof PATIENT_TABS)[number];

function isValidTab(value: string | undefined): value is PatientTab {
  return PATIENT_TABS.includes(value as PatientTab);
}

/** Tab shown on first paint from URL (visit detail route takes precedence). */
function initialTabFromUrl(params: {
  visitId?: string;
  tab?: string;
}): PatientTab {
  if (params.visitId) return 'visits';
  if (isValidTab(params.tab)) return params.tab;
  return 'visits';
}

/**
 * Whether a visit task's type has a wound / goals section. `visit_type` is typed
 * as a loose string on the task payload, so narrow it before consulting the
 * capability lists: an unset or unrecognized value means "no section" rather
 * than a crash.
 */
const taskSupportsWounds = (task: TaskListResponse): boolean =>
  !!task.visit_type && supportsWounds(task.visit_type as VisitTypeId);

const taskShowsGoalsSection = (task: TaskListResponse): boolean =>
  !!task.visit_type && showsGoalsSection(task.visit_type as VisitTypeId);

const PatientDetail: React.FC = () => {
  const {
    id,
    tab: tabParam,
    visitId: visitIdParam,
  } = useParams<{
    id: string;
    tab?: string;
    visitId?: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const episodeIdFromUrl = searchParams.get('episode');
  const { success: showSuccess, error: showError } = useToast();
  const { options: visitTypeOptions } = useVisitTypeOptions();
  const { printTask, printingTaskId } = usePrintView();
  // A second instance for the deleted list. The live one must stay unwidened,
  // so a task deleted in another tab stops printing rather than printing on
  // regardless. Only one of the two lists is on screen at a time, so the two
  // "which row is printing" states never compete.
  const { printTask: printDeletedTask, printingTaskId: printingDeletedTaskId } =
    usePrintView({ includeDeleted: true });
  // Drives the synchronous-tab-open batch "Combined PDF" export.
  const { exportPdf: exportBatchPdf } = usePdfExport();
  const isMobile = useIsMobile();

  const currentPatientIdRef = useRef(id);
  currentPatientIdRef.current = id;
  const previousPatientIdRef = useRef(id);

  const [selectedVisitId, setSelectedVisitId] = useState<string | null>(
    visitIdParam ?? null
  );
  // Active tab for main content area — synced with URL
  const [activeTab, setActiveTabState] = useState<PatientTab>(() =>
    initialTabFromUrl({
      visitId: visitIdParam,
      tab: tabParam,
    })
  );

  // Returns the page to the task list, for a restore performed from a detail
  // view: the panel is now showing a live task in its read-only deleted mode.
  const closeTaskDetail = useCallback(() => {
    setSelectedVisitId(null);
    navigate(`/patients/${id}`);
  }, [id, navigate]);

  const {
    episodes,
    setEpisodes,
    canAddEpisode,
    episodesSettledPatientId,
    setSelectedEpisodeId,
    effectiveSelectedEpisodeId,
    selectedEpisode,
    previousEpisode,
    otherEpisodes,
    lupaPeriods,
    lupaLoading,
    loadEpisodes,
  } = usePatientDetailEpisodes({
    patientId: id,
    episodeIdFromUrl,
    currentPatientIdRef,
  });

  const {
    tasks,
    setTasks,
    allTasks,
    visitTasks,
    visitsLoading,
    visitsError,
    tasksPatientId,
    tasksRefreshVersion,
    fetchTasks,
    taskView,
    setTaskView,
    deletedTasks,
    deletedTasksLoading,
    deletedTasksError,
    deletedTasksReady,
    deletedTaskById,
    linkedDeletedTask,
    restoringTaskId,
    handleRestoreTask,
    tableTaskTypeFilter,
    setTableTaskTypeFilterFromUser,
    tasksForEpisode,
    episodeVisitTasks,
    displayedTasks,
    calendarTasks,
    documentContextTasks,
    documentsEpisodeScope,
    selectedTaskIds,
    setSelectedTaskIds,
    selectedTasks,
    atSelectionCap,
    selectAllState,
    toggleTaskSelected,
    removeTaskFromSelection,
    clearTaskSelection,
    handleToggleSelectAll,
  } = usePatientDetailTasks({
    patientId: id,
    selectedVisitId,
    effectiveSelectedEpisodeId,
    episodesSettledPatientId,
    currentPatientIdRef,
    closeTaskDetail,
    showSuccess,
    showError,
  });

  const { addTaskTargetEpisode, addTaskTargetClinicId } =
    useAddTaskTargetEpisode({
      patientId: id,
      episodeIdFromUrl,
      selectedVisitId,
      episodes,
      selectedEpisode,
      effectiveSelectedEpisodeId,
      setSelectedEpisodeId,
      tasks,
      linkedDeletedTask,
      visitsLoading,
      tasksPatientId,
      currentPatientIdRef,
    });

  const [patient, setPatient] = useState<PatientResponse | null>(null);
  const [patientInsurances, setPatientInsurances] = useState<
    PatientInsuranceResponse[]
  >([]);
  const [insurancesLoading, setInsurancesLoading] = useState(true);
  const [insurancePopupOpen, setInsurancePopupOpen] = useState(false);
  const [editingInsuranceId, setEditingInsuranceId] = useState<string | null>(
    null
  );
  const [insuranceRefreshTrigger, setInsuranceRefreshTrigger] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [communications, setCommunications] = useState<
    PatientCommunicationResponse[]
  >([]);
  const [isEpisodeEditorOpen, setIsEpisodeEditorOpen] = useState(false);
  const [isEpisodeInsuranceEditorOpen, setIsEpisodeInsuranceEditorOpen] =
    useState(false);
  const [isEpisodeWorksheetOpen, setIsEpisodeWorksheetOpen] = useState(false);
  const [isFaceSheetOpen, setIsFaceSheetOpen] = useState(false);
  const [clinicians, setClinicians] = useState<AvailableClinician[]>([]);
  const [physiciansByClinic, setPhysiciansByClinic] = useState<
    Record<string, ClinicPhysicianResponse[]>
  >({});
  const documentsRef = useRef<PatientDocumentsTabHandle>(null);
  const communicationsFetchedRef = useRef(false);
  const loadedCommunicationTaskLabelsKeyRef = useRef<string | null>(null);
  const [updatingTaskId, setUpdatingTaskId] = useState<string | null>(null);
  // Identifies the row whose last edit the server rejected, and how many times.
  // Feeding the counter into that row's date-picker `key` remounts it so it
  // re-reads the server value instead of keeping the draft the server refused;
  // the picker only re-syncs its text when its `date` prop changes, and a
  // rejected save leaves that prop untouched. Scoped to the one row so a
  // background refresh never interrupts an edit in progress elsewhere.
  const [rejectedEdit, setRejectedEdit] = useState<{
    taskId: string;
    nonce: number;
  } | null>(null);

  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  // The day an Add Task opened from the calendar lands on. Only read when the
  // dialog mounts, which is what makes "reset on open" the dialog's own job.
  const [addTaskInitialDate, setAddTaskInitialDate] = useState<
    Date | undefined
  >(undefined);
  const [isScheduleVisitsOpen, setIsScheduleVisitsOpen] = useState(false);
  const communicationRequestIdRef = useRef(0);

  const [deleteTask, setDeleteTask] = useState<TaskListResponse | null>(null);
  // EMR-325: which visit's EVV readings are open. One dialog at page level
  // rather than one per row, mirroring how Hotbox drives the same component.
  const [evvVisitId, setEvvVisitId] = useState<string | null>(null);
  const [isDeletingTask, setIsDeletingTask] = useState(false);

  // Scheduling Points (EMR-294): what each candidate is already carrying,
  // beside their name at the moment work is assigned.
  const canViewSchedulingPointLoads = useCanViewSchedulingPointLoads();
  const invalidateSchedulingPointLoads = useInvalidateSchedulingPointLoads();
  // `canViewSchedulingPointLoads` is the whole gate. Deliberately *not* ANDed
  // with the patient's own clinic: the clinic that prices a week is the one on
  // the task or episode being scheduled (`task.agency_clinic_id` /
  // `addTaskTargetClinicId`), and those can differ from `patient.clinic_id`.
  // Gating on the patient got it wrong in both directions — it hid computable
  // loads for a clinic-less patient whose episode had a clinic, and it left the
  // badge silently missing in the reverse case while the explanatory line,
  // keyed on the patient, stayed hidden. Each call site checks the clinic it
  // actually prices by; `patientClinicId` now only picks which sentence is true.
  const patientClinicId = patient?.clinic_id ?? null;

  // A boundary-defining visit (SOC or DC/TRN/DTH) sitting on an episode date was
  // moved off it, so we offer to move the matching episode date to match.
  const [episodeDateSync, setEpisodeDateSync] = useState<{
    boundary: EpisodeBoundary;
    episodeId: string;
    visitId: string;
    newDate: string;
    currentBoundaryDate: string;
    episodeStartDate: string | null;
    episodeEndDate: string | null;
    visitLabel?: string;
  } | null>(null);

  const [addEpisodeOpen, setAddEpisodeOpen] = useState(false);

  const communicationTaskIdsKey = useMemo(
    () =>
      tasks
        .filter(task => task.task_type === 'patient_communication')
        .map(task => task.id)
        .sort()
        .join('|'),
    [tasks]
  );

  const setActiveTab = (tab: PatientTab) => {
    setActiveTabState(tab);
    if (!id) return;
    let path: string;
    const selectedNavigationTask = tasks.find(
      task => task.id === selectedVisitId
    );
    if (
      (tab === 'wounds' || tab === 'goals') &&
      selectedNavigationTask &&
      selectedNavigationTask.task_type !== 'visit'
    ) {
      setSelectedVisitId(null);
    }
    if (
      tab === 'visits' &&
      selectedNavigationTask &&
      selectedNavigationTask.task_type !== 'patient_communication'
    ) {
      path = `/patients/${id}/visits/${selectedVisitId}`;
    } else if (tab === 'visits') {
      setSelectedVisitId(null);
      path = `/patients/${id}`;
    } else {
      path = `/patients/${id}/${tab}`;
    }
    navigate(path, { replace: true });
  };

  useEffect(() => {
    if (visitIdParam) {
      setSelectedVisitId(visitIdParam);
      setActiveTabState('visits');
      return;
    }
    const resolved = isValidTab(tabParam) ? tabParam : 'visits';
    setActiveTabState(resolved);
    if (resolved === 'visits') {
      setSelectedVisitId(null);
    }
  }, [tabParam, visitIdParam]);

  /**
   * Reload the patient row through the same path the page first loads it by.
   *
   * Hoisted out of the mount effect because a child can write to the patient
   * record — carrying forward an imported "no known allergies" assertion sets
   * `patients.no_known_allergies` — and the controls that read it stay stale
   * until this runs. One fetch, one place, so a refreshed chart and a freshly
   * loaded one cannot disagree.
   *
   * `initial` marks the mount call. Only that call owns the page's loading and
   * error states: a background refresh that fails must not blank a chart the
   * clinician is already reading.
   */
  const refreshPatient = useCallback(
    async (options?: { initial?: boolean }) => {
      if (!id) return;
      const initial = options?.initial ?? false;
      const patientId = id;

      try {
        const patientData = await getPatient({
          path: { patient_id: patientId },
        });
        if (currentPatientIdRef.current !== patientId) {
          return;
        }
        if (patientData && patientData.data) {
          setPatient(patientData.data);
        }
      } catch (err) {
        if (currentPatientIdRef.current !== patientId) {
          return;
        }
        console.error('Error fetching patient:', err);
        if (initial) {
          setError('Failed to load patient details');
        }
      } finally {
        if (initial && currentPatientIdRef.current === patientId) {
          setLoading(false);
        }
      }
    },
    [id]
  );

  useEffect(() => {
    if (!id) {
      setLoading(false);
      setInsurancesLoading(false);
      return;
    }

    // Clear patient-scoped state before fetching so stale insurances from the
    // previous patient cannot bleed into PatientHeader.
    setPatient(null);
    setPatientInsurances([]);
    setInsurancesLoading(true);
    // The summary sheet is scoped to one patient, so leaving it open across a
    // navigation would render the arriving patient under the departing one's
    // heading.
    setIsFaceSheetOpen(false);
    setLoading(true);
    setError(null);

    const fetchPatientInsurances = async () => {
      const patientId = id;
      try {
        const res = await listPatientInsurances({
          path: { patient_id: patientId },
        });
        if (currentPatientIdRef.current !== patientId) {
          return;
        }
        if (res.data) setPatientInsurances(res.data);
      } catch (err) {
        if (currentPatientIdRef.current !== patientId) {
          return;
        }
        console.error('Failed to load patient insurances:', err);
      } finally {
        if (currentPatientIdRef.current === patientId) {
          setInsurancesLoading(false);
        }
      }
    };

    void refreshPatient({ initial: true });
    fetchPatientInsurances();
  }, [id, refreshPatient]);

  // Refetch the copy of the insurance list that feeds PatientHeader and
  // EpisodeInsuranceSelector, for changes made outside the add/edit dialog.
  const refreshPatientInsurances = useCallback(async () => {
    if (!id) return;
    const patientId = id;
    try {
      const res = await listPatientInsurances({
        path: { patient_id: patientId },
      });
      if (currentPatientIdRef.current !== patientId) return;
      if (res.data) setPatientInsurances(res.data);
    } catch (err) {
      console.error('Failed to load patient insurances:', err);
    }
  }, [id]);

  useEffect(() => {
    communicationsFetchedRef.current = false;
    loadedCommunicationTaskLabelsKeyRef.current = null;
    setCommunications([]);
  }, [id]);

  useEffect(() => {
    const loadCommunications = async () => {
      if (!id) return;
      const patientId = id;
      const taskRowsReady = tasksPatientId === id;
      const needsCommunicationLabels = communicationTaskIdsKey !== '';
      const communicationTaskLabelsKey =
        taskRowsReady && needsCommunicationLabels
          ? `${communicationTaskIdsKey}:${tasksRefreshVersion}`
          : null;
      const communicationTaskLabelsStale =
        communicationTaskLabelsKey !== null &&
        loadedCommunicationTaskLabelsKeyRef.current !==
          communicationTaskLabelsKey;
      const shouldLoadForTaskLabels =
        communicationTaskLabelsStale && needsCommunicationLabels;

      if (!shouldLoadForTaskLabels) {
        if (taskRowsReady && !needsCommunicationLabels) {
          loadedCommunicationTaskLabelsKeyRef.current = null;
          communicationsFetchedRef.current = false;
          setCommunications([]);
        }
        return;
      }
      const requestId = communicationRequestIdRef.current + 1;
      communicationRequestIdRef.current = requestId;
      const isLatestCommunicationRequest = () =>
        currentPatientIdRef.current === patientId &&
        communicationRequestIdRef.current === requestId;

      try {
        const res = await listPatientCommunications({
          path: { patient_id: patientId },
        });
        if (!isLatestCommunicationRequest()) {
          return;
        }
        setCommunications(res.data ?? []);
        communicationsFetchedRef.current = true;
        loadedCommunicationTaskLabelsKeyRef.current =
          communicationTaskLabelsKey;
      } catch (err) {
        if (!isLatestCommunicationRequest()) {
          return;
        }
        console.error('Error fetching communications:', err);
        if (!communicationsFetchedRef.current) {
          setCommunications([]);
        }
      } finally {
        // no loading state to clear
      }
    };
    void loadCommunications();
  }, [id, tasksPatientId, communicationTaskIdsKey, tasksRefreshVersion]);

  useEffect(() => {
    if (!patient) return;
    // Clear immediately so stale data from a previous clinic doesn't bleed
    // through while the new fetch is in-flight.
    setClinicians([]);
    let cancelled = false;
    const fetchClinicians = async () => {
      try {
        const response = await getAvailableClinicians({
          query: patient.clinic_id ? { clinic_id: patient.clinic_id } : {},
        });
        if (!cancelled && response?.data) {
          setClinicians(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching clinicians:', err);
          showError(
            'Load Error',
            'Unable to load clinicians. Please try again.'
          );
        }
      }
    };
    void fetchClinicians();
    return () => {
      cancelled = true;
    };
  }, [patient?.clinic_id, showError]);

  /**
   * Physicians from the new ClinicPhysician model. Cached per-clinic and
   * lazily fetched on first lookup for that clinic.
   */
  useEffect(() => {
    const clinicId = patient?.clinic_id;
    if (!clinicId) return;
    if (physiciansByClinic[clinicId] !== undefined) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await listAgencyClinicPhysicians({
          path: { agency_clinic_id: clinicId },
        });
        if (!cancelled) {
          setPhysiciansByClinic(prev => ({
            ...prev,
            [clinicId]: response?.data ?? [],
          }));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching physicians for clinic:', err);
          setPhysiciansByClinic(prev => ({ ...prev, [clinicId]: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patient?.clinic_id, physiciansByClinic]);

  // The Add Task dialog prices its assignees by the episode's clinic, which can
  // differ from the patient's, so warm that roster too.
  useEffect(() => {
    const clinicId = addTaskTargetClinicId;
    if (!clinicId) return;
    if (physiciansByClinic[clinicId] !== undefined) return;

    let cancelled = false;
    (async () => {
      try {
        const response = await listAgencyClinicPhysicians({
          path: { agency_clinic_id: clinicId },
        });
        if (!cancelled) {
          setPhysiciansByClinic(prev => ({
            ...prev,
            [clinicId]: response?.data ?? [],
          }));
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Error fetching physicians for episode clinic:', err);
          setPhysiciansByClinic(prev => ({ ...prev, [clinicId]: [] }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [addTaskTargetClinicId, physiciansByClinic]);

  const formatDate = (dateString: string | null | undefined) => {
    return formatDateToMMDDYYYY(dateString) || 'N/A';
  };

  const isOrder = useCallback(
    (taskId: string) => {
      const task = tasks.find(t => t.id === taskId);
      return task?.task_type === 'order';
    },
    [tasks]
  );

  const isCommunication = useCallback(
    (taskId: string) => {
      const task = tasks.find(t => t.id === taskId);
      return task?.task_type === 'patient_communication';
    },
    [tasks]
  );

  const isMiscTask = useCallback(
    (taskId: string) => {
      const task = tasks.find(t => t.id === taskId);
      return task?.task_type === 'misc_task';
    },
    [tasks]
  );

  const handleTaskUpdate = async (
    taskId: string,
    updates: {
      assigned_clinician_id?: string | null;
      date?: string;
      scheduled_date?: string;
    },
    successMessage: string
  ) => {
    setUpdatingTaskId(taskId);

    const applyLocalPatch = (patch: Partial<TaskListResponse>) => {
      setTasks(prev =>
        prev.map(task => (task.id === taskId ? { ...task, ...patch } : task))
      );
    };

    const assigneePatch = (): Partial<TaskListResponse> =>
      updates.assigned_clinician_id !== undefined
        ? {
            assigned_clinician_id: updates.assigned_clinician_id,
            // Clear the cached name so the label recomputes from the new id.
            assigned_clinician_name: null,
          }
        : {};

    const refreshTasksAndNotify = async (patch: Partial<TaskListResponse>) => {
      // The update already succeeded, so update the row in place, notify, then
      // reconcile with a silent refresh. Local patching keeps the row correct
      // if that background refresh fails, and never reloads the entire visits
      // section.
      applyLocalPatch(patch);
      showSuccess('Task Updated', successMessage);
      // The workload numbers just became wrong. Dropping the cached weeks here
      // rather than waiting out the stale window is the difference between a
      // scheduler watching the number move as they assign and a scheduler
      // concluding the number is decorative.
      void invalidateSchedulingPointLoads();
      // Re-enable the row's controls before the background refresh so the user
      // is not blocked from further edits while it reconciles. fetchTasks
      // guards concurrent requests on its own via taskRequestIdRef.
      setUpdatingTaskId(null);
      await fetchTasks({ silent: true });
    };

    const reportUpdateFailure = async (failure: unknown) => {
      console.error('Error updating task:', failure);
      // A visit date move returns 422 both when the target date falls outside
      // the episode window and when no authorization covers it (EMR-706). The
      // server says which, so prefer its message and keep the episode copy as
      // the fallback for a 422 with no detail. Scoped to date moves so an
      // unrelated 422 on another update does not borrow the episode copy.
      if (
        updates.scheduled_date !== undefined &&
        getHttpStatus(failure) === 422
      ) {
        const serverMessage = getBusinessErrorMessage(failure);
        if (serverMessage) {
          showError('Unable to Move Task', serverMessage);
        } else {
          showError(
            'Date Outside Episode',
            'The selected date is outside the episode window.'
          );
        }
      } else {
        // Prefer the server's 4xx business message — the billing-cycle lock
        // (409) explains exactly why the date is frozen, which the generic
        // copy cannot. getBusinessErrorMessage withholds 5xx/proxy bodies.
        showError(
          'Update Failed',
          getBusinessErrorMessage(failure) ?? 'Failed to update task.'
        );
      }
      // Nothing was persisted, so pull the row back to server truth and discard
      // the rejected draft still sitting in the row's date picker.
      setRejectedEdit(prev => ({
        taskId,
        nonce: (prev?.taskId === taskId ? prev.nonce : 0) + 1,
      }));
      await fetchTasks({ silent: true });
    };

    // The generated client resolves rather than throws on a non-2xx (its
    // ThrowOnError type parameter defaults to false), so a rejected save
    // arrives here as a result with `error` set and no `data`. Route it to the
    // same reporting path as a thrown network error instead of silently
    // treating it as a no-op.
    const settleUpdate = async <T,>(
      result: { data?: T },
      buildPatch: (data: T) => Partial<TaskListResponse>
    ) => {
      if (result.data) {
        await refreshTasksAndNotify(buildPatch(result.data));
        return;
      }
      await reportUpdateFailure(result);
    };

    try {
      if (isCommunication(taskId)) {
        const response = await updatePatientCommunication({
          path: { communication_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            communication_date: updates.date,
          },
        });
        await settleUpdate(response, () => ({
          ...(updates.date !== undefined ? { task_date: updates.date } : {}),
          ...assigneePatch(),
        }));
      } else if (isMiscTask(taskId)) {
        const response = await updateMiscTask({
          path: { misc_task_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            task_date: updates.date,
          },
        });
        await settleUpdate(response, () => ({
          ...(updates.date !== undefined ? { task_date: updates.date } : {}),
          ...assigneePatch(),
        }));
      } else if (isOrder(taskId)) {
        const response = await updateOrder({
          path: { order_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            order_date: updates.date,
          },
        });
        await settleUpdate(response, () => ({
          ...(updates.date !== undefined ? { task_date: updates.date } : {}),
          ...assigneePatch(),
        }));
      } else {
        const response = await updateVisit({
          path: { visit_id: taskId },
          body: {
            assigned_clinician_id: updates.assigned_clinician_id,
            ...(updates.scheduled_date !== undefined
              ? { scheduled_date: updates.scheduled_date }
              : {}),
          },
        });
        // Visit rows show task_date as the service date. When the target moves,
        // take both dates from the update response — visit_date may stay put
        // once charted times are already set.
        await settleUpdate(response, data => ({
          ...(updates.scheduled_date !== undefined
            ? {
                scheduled_date: data.scheduled_date,
                task_date: data.visit_date,
              }
            : {}),
          ...assigneePatch(),
        }));
      }
    } catch (err) {
      await reportUpdateFailure(err);
    } finally {
      setUpdatingTaskId(null);
    }
  };

  const handleAssigneeChange = (taskId: string, clinicianId: string) => {
    handleTaskUpdate(
      taskId,
      { assigned_clinician_id: clinicianId },
      'Assignee updated successfully.'
    );
  };

  const handleTargetDateChange = (
    taskId: string,
    newDate: Date | undefined
  ) => {
    if (!newDate) return;
    const formattedDate = formatDateToISO(newDate);
    if (!formattedDate) return;
    const task = tasks.find(t => t.id === taskId);
    if (task && isVisitTaskRow(task)) {
      // If this visit currently sits on the episode boundary it defines
      // (SOC on start, DC/TRN/DTH on end) and is being moved to a different
      // date, offer to move the episode boundary too. The episode must move
      // first because the reschedule endpoint rejects a date outside the current
      // window, so the offer dialog drives both updates in order.
      const boundary = activeEpisodeBoundaryForVisit(task);
      const boundaryDate =
        boundary === 'start'
          ? task.episode_start_date
          : boundary === 'end'
            ? task.episode_end_date
            : null;
      if (boundary && boundaryDate && formattedDate !== boundaryDate) {
        setEpisodeDateSync({
          boundary,
          episodeId: task.episode_id,
          visitId: task.id,
          newDate: formattedDate,
          currentBoundaryDate: boundaryDate,
          episodeStartDate: task.episode_start_date ?? null,
          episodeEndDate: task.episode_end_date ?? null,
          visitLabel: task.visit_type
            ? OASIS_VISIT_TYPE_LABELS[task.visit_type]
            : undefined,
        });
        return;
      }
      handleTaskUpdate(
        taskId,
        { scheduled_date: formattedDate },
        'Target date updated successfully.'
      );
      return;
    }
    handleTaskUpdate(
      taskId,
      { date: formattedDate },
      'Target date updated successfully.'
    );
  };

  /**
   * Selectable range for a task row's target-date picker. Most tasks are confined
   * to the episode window. A visit sitting on the boundary it defines may move the
   * boundary with it, so that side opens up — but only as far as the episode rules
   * allow (60-day cap, end after start, no overlap with a neighbouring episode),
   * so dates the sync endpoint would reject cannot be picked in the first place.
   */
  const taskTargetDateBounds = useCallback(
    (
      task: TaskListResponse
    ): { minDate: Date | undefined; maxDate: Date | undefined } => {
      const boundary = activeEpisodeBoundaryForVisit(task);
      if (boundary && task.episode_start_date && task.episode_end_date) {
        const bounds = boundaryRescheduleBounds({
          boundary,
          episodeId: task.episode_id,
          episodeStartDate: task.episode_start_date,
          episodeEndDate: task.episode_end_date,
          patientEpisodes: episodes,
        });
        return {
          minDate: parseISOToLocalDate(bounds.minDate),
          maxDate: parseISOToLocalDate(bounds.maxDate),
        };
      }
      return {
        minDate: parseISOToLocalDate(task.episode_start_date),
        maxDate: parseISOToLocalDate(task.episode_end_date),
      };
    },
    [episodes]
  );

  const handleDeleteTask = async () => {
    if (!deleteTask) return;

    setIsDeletingTask(true);
    try {
      const deleteTaskId = deleteTask.id;
      const response = await deleteTaskRequest(deleteTask);
      if (response.error) {
        console.error('Error deleting task:', response.error);
        // A 409 is the only error carrying a ConflictCode, and it is the one
        // the user can act on — "this is signed" reads very differently from
        // a flat failure. Everything else falls back to the generic sentence
        // (EMR-1312).
        showError(
          'Delete Failed',
          deleteConflictSentence(deleteConflictCode(response))
        );
        return;
      }
      setTasks(prev => prev.filter(t => t.id !== deleteTaskId));
      setDeleteTask(null);
      // A deleted task leaves its clinician's week — the cached load would
      // otherwise keep counting work that no longer exists for up to a minute.
      void invalidateSchedulingPointLoads();
      showSuccess('Task Deleted', 'Task deleted successfully.');
    } catch (err) {
      console.error('Error deleting task:', err);
      showError('Delete Failed', 'Failed to delete task.');
    } finally {
      setIsDeletingTask(false);
    }
  };

  const assignableClinicians = useMemo(
    () => clinicians.filter(c => c.is_assignable_clinician),
    [clinicians]
  );

  const getClinicPhysicians = useMemo(() => {
    return (clinicId: string | null | undefined): ClinicPhysicianResponse[] => {
      if (!clinicId) return [];
      return physiciansByClinic[clinicId] ?? [];
    };
  }, [physiciansByClinic]);

  const getClinicAssignableClinicians = useMemo(() => {
    const dedup = (
      list: typeof assignableClinicians,
      preserveId?: string | null
    ) => {
      const seen = new Set<string>();
      const result: typeof assignableClinicians = [];

      const preserved = preserveId
        ? list.find(c => c.id === preserveId)
        : undefined;
      if (preserved) {
        result.push(preserved);
        seen.add(preserved.user_id);
      }

      for (const c of list) {
        if (c.id === preserveId || seen.has(c.user_id)) continue;
        seen.add(c.user_id);
        result.push(c);
      }
      return result;
    };
    return (
      clinicId: string | null | undefined,
      discipline?: (typeof TASK_TYPE_DISCIPLINE_MAP)[TaskTypeId],
      alwaysIncludeClinicianId?: string | null
    ) => {
      const filtered = assignableClinicians.filter(c => {
        if (alwaysIncludeClinicianId && c.id === alwaysIncludeClinicianId) {
          return true;
        }
        if (clinicId && c.clinic_id !== clinicId) return false;
        return clinicianMatchesDiscipline(c, discipline);
      });
      return dedup(filtered, alwaysIncludeClinicianId);
    };
  }, [assignableClinicians]);

  const getAssignedClinicianName = useCallback(
    (task: TaskListResponse) => {
      if (!task.assigned_clinician_id) return 'Unassigned';

      if (task.assigned_clinician_name) {
        return task.assigned_clinician_name;
      }

      const clinician = getClinicAssignableClinicians(
        task.agency_clinic_id,
        getAssigneeDisciplineForTask(task),
        task.assigned_clinician_id
      ).find(c => c.id === task.assigned_clinician_id);

      return clinician
        ? formatClinicianName(clinician)
        : 'Assigned clinician unavailable';
    },
    [getClinicAssignableClinicians]
  );

  // Names that appear more than once (different users, same display name).
  // Deduplicate by user_id first to avoid false positives from the same
  // clinician appearing in multiple clinics.
  const duplicateClinicianNames = useMemo(() => {
    const seen = new Set<string>();
    const deduped = assignableClinicians.filter(c => {
      if (seen.has(c.user_id)) return false;
      seen.add(c.user_id);
      return true;
    });
    const counts = new Map<string, number>();
    for (const c of deduped) {
      const name = formatClinicianName(c);
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    const dupes = new Set<string>();
    for (const [name, count] of counts) {
      if (count > 1) dupes.add(name);
    }
    return dupes;
  }, [assignableClinicians]);

  const formatDuplicateClinicianOptionLabel = useCallback(
    (clinician: ClinicianSelectInput, label: string) => {
      if (duplicateClinicianNames.has(label) && clinician.user_email) {
        return `${label} (${clinician.user_email})`;
      }
      return label;
    },
    [duplicateClinicianNames]
  );

  const communicationById = useMemo(
    () =>
      new Map(
        communications.map(communication => [communication.id, communication])
      ),
    [communications]
  );

  // The fetch, its request-id guard and its first-fetch-only placeholder live
  // in the hook so they can be tested without standing up this page.
  const {
    budget: episodeAuthBudget,
    requiresAuthorization: episodeRequiresAuthorization,
    errors: authorizationErrors,
    errorCount: authorizationErrorCount,
    unavailable: authBudgetUnavailable,
    loading: authBudgetLoading,
    refresh: fetchAuthErrors,
  } = useAuthorizationBudget(effectiveSelectedEpisodeId);

  const handleUpdateEpisodeInsurance = useCallback(
    async (insuranceId: string) => {
      if (!effectiveSelectedEpisodeId) return;

      try {
        await updateEpisodeAssignments({
          path: { episode_id: effectiveSelectedEpisodeId },
          body: { patient_insurance_id: insuranceId },
        });

        // Reload episodes to get updated insurance info
        await loadEpisodes();
        showSuccess('Episode insurance updated successfully');
      } catch (error) {
        console.error('Failed to update episode insurance:', error);
        const errorMessage =
          getBusinessErrorMessage(error) ??
          'Failed to update episode insurance';
        showError(errorMessage);
        throw error;
      }
    },
    [effectiveSelectedEpisodeId, loadEpisodes, showSuccess, showError]
  );

  // Physicians for the summary sheet. The episode's clinic can differ from the
  // patient's, and the episode's physician sits on that clinic's roster, so
  // prefer it and keep the patient's clinic as the fallback. Both rosters are
  // already cached per clinic, so this costs no request. The clinic comes off a
  // task on the episode, which is where the add-task flow reads it from too.
  const faceSheetPhysicians = useMemo(() => {
    const episodeClinicId = selectedEpisode
      ? tasks.find(task => task.episode_id === selectedEpisode.id)
          ?.agency_clinic_id
      : undefined;
    return getClinicPhysicians(episodeClinicId ?? patient?.clinic_id);
  }, [selectedEpisode, tasks, getClinicPhysicians, patient?.clinic_id]);

  const targetEpisodeBlocksVisits =
    episodeBlocksNewVisits(addTaskTargetEpisode);

  // Unmounting the dialog never fires onOpenChange, so without this the open
  // flag would survive and pop the dialog back up on the next episode that
  // does accept visits.
  useEffect(() => {
    if (!addTaskTargetEpisode || targetEpisodeBlocksVisits) {
      setIsScheduleVisitsOpen(false);
    }
  }, [addTaskTargetEpisode, targetEpisodeBlocksVisits]);

  const openAddTaskDialog = useCallback((initialDate?: Date) => {
    setAddTaskInitialDate(initialDate);
    setIsAddDialogOpen(true);
  }, []);

  useEffect(() => {
    if (previousPatientIdRef.current === id) {
      return;
    }

    previousPatientIdRef.current = id;
    setDeleteTask(null);
    setIsAddDialogOpen(false);
  }, [id]);

  useEffect(() => {
    if (!deleteTask) {
      return;
    }
    if (!tasks.some(task => task.id === deleteTask.id)) {
      setDeleteTask(null);
    }
  }, [deleteTask, tasks]);

  const episodeStatusBadgeVariant = (
    status: string | null | undefined
  ): 'default' | 'secondary' | 'destructive' | 'outline' => {
    switch (status) {
      case 'active':
        return 'default';
      case 'completed':
        return 'secondary';
      case 'cancelled':
      case 'interrupted':
        return 'destructive';
      default:
        return 'outline';
    }
  };

  // Display-only relabel: a cancelled episode reads as "Inactive" (EMR-158). The
  // stored value and badge variant mapping are unchanged.
  const episodeStatusDisplayLabel = (
    status: string | null | undefined
  ): string => (status === 'cancelled' ? 'Inactive' : (status ?? ''));

  const getTaskDisplayName = useCallback(
    (task: TaskListResponse) => {
      if (task.task_type !== 'patient_communication') {
        return task.display_name;
      }

      return (
        communicationById.get(task.id)?.communication_type ??
        'Patient Communication'
      );
    },
    [communicationById]
  );

  const getTaskAssigneeLabel = useCallback(
    (task: TaskListResponse) => getAssignedClinicianName(task),
    [getAssignedClinicianName]
  );

  // Options for a task row's assignee picker.
  //
  // An imported chart can already name a clinician who has never signed in
  // (EMR-1264). Those staff rows are absent from `/user-clinics/available`, so
  // nobody can be *newly* assigned to them — but a row already assigned to one
  // would find no option matching its own value and fall back to the
  // "Unassigned" placeholder, which is a confident false statement about a
  // task that is assigned. So the current assignee is added back, alone: every
  // other unclaimed person stays out of the list.
  const getTaskAssigneeClinicians = useCallback(
    (task: TaskListResponse): ClinicianSelectInput[] => {
      const available = getClinicAssignableClinicians(
        task.agency_clinic_id,
        getAssigneeDisciplineForTask(task),
        task.assigned_clinician_id
      );
      const assignedId = task.assigned_clinician_id;
      if (
        !assignedId ||
        task.assigned_clinician_unclaimed_import !== true ||
        available.some(c => c.id === assignedId)
      ) {
        return available;
      }
      return [
        {
          id: assignedId,
          // The name the row already displays when it is read-only, so the
          // two branches of this cell never disagree. It arrives formatted
          // (credentials appended by the API), which is why it goes in whole
          // rather than being split into first/last for reformatting.
          user_first_name: getTaskAssigneeLabel(task),
          user_last_name: '',
          is_assignable_clinician: true,
        },
        ...available,
      ];
    },
    [getClinicAssignableClinicians, getTaskAssigneeLabel]
  );

  const isThirtyDaySummaryTask = useCallback(
    (taskId: string) =>
      communicationById.get(taskId)?.communication_type ===
      THIRTY_DAY_SUMMARY_COMMUNICATION_TYPE,
    [communicationById]
  );

  // ===== Batch actions for the episode task table (ROG-2506) =====
  // The selection is snapshotted when the notes dialog opens rather than read
  // live: the refresh that follows a successful append replaces every task
  // object, which would otherwise change the dialog's own count out from under
  // the toast it triggers.
  const [batchNotesTasks, setBatchNotesTasks] = useState<TaskListResponse[]>(
    []
  );
  const [batchNotesOpen, setBatchNotesOpen] = useState(false);

  // Opens the dialog with the whole selection, not just the eligible rows, so
  // it can disclose what it is about to skip.
  const runBatchAddNote = useCallback((items: TaskListResponse[]) => {
    setBatchNotesTasks(items);
    setBatchNotesOpen(true);
  }, []);

  const runBatchDelete = useCallback(
    async (items: TaskListResponse[]) => {
      const { deletable } = partitionDeletable(items);
      if (deletable.length === 0) return;
      const results = await Promise.allSettled(
        deletable.map(task => deleteTaskRequest(task))
      );
      const succeededIds: string[] = [];
      const failures: { task: TaskListResponse; code?: string }[] = [];
      results.forEach((result, index) => {
        const task = deletable[index];
        if (result.status === 'fulfilled' && !result.value.error) {
          succeededIds.push(task.id);
        } else {
          const code =
            result.status === 'fulfilled'
              ? getConflictCode(result.value.error)
              : undefined;
          failures.push({ task, code });
        }
      });
      if (succeededIds.length > 0) {
        const succeeded = new Set(succeededIds);
        setTasks(prev => prev.filter(t => !succeeded.has(t.id)));
        setSelectedTaskIds(prev => {
          const next = new Set(prev);
          succeededIds.forEach(taskId => next.delete(taskId));
          return next;
        });
      }
      if (failures.length === 0) {
        showSuccess(
          'Tasks deleted',
          `${succeededIds.length} task${succeededIds.length === 1 ? '' : 's'} deleted.`
        );
        return;
      }
      const lines = failures
        .slice(0, 3)
        .map(
          f => `${getTaskDisplayName(f.task)}: ${deleteConflictCopy(f.code)}`
        );
      const more =
        failures.length > 3 ? ` …and ${failures.length - 3} more.` : '';
      showError(
        `Couldn't delete ${failures.length} of ${deletable.length}`,
        lines.join('; ') + more
      );
    },
    [getTaskDisplayName, setSelectedTaskIds, setTasks, showSuccess, showError]
  );

  const runBatchPrint = useCallback(
    (items: TaskListResponse[], format: 'pdf' | 'zip', choiceKey?: string) => {
      const chosen =
        choiceKey === 'completed' ? partitionNotStarted(items).started : items;
      // Drop task types the endpoint cannot render before the length guard:
      // filtering afterwards would send an empty task list (rejected as a 422)
      // and, for the PDF branch, leave an already-opened blank tab behind.
      const { printable: printSet, skippedUnprintable } =
        partitionPrintable(chosen);
      const episodeId = effectiveSelectedEpisodeId;
      if (!episodeId) return;
      if (printSet.length === 0) {
        showError(
          'Nothing to print',
          'The selected tasks have no printable document.'
        );
        return;
      }
      // Both branches call the endpoint identically except for `format`; only
      // the delivery differs (open in a tab vs. download).
      const callBatchPrint = () =>
        batchPrintEpisodeTasks({
          path: { episode_id: episodeId },
          body: { tasks: toBatchPrintTaskRefs(printSet), format },
        });
      if (format === 'pdf') {
        // exportBatchPdf opens the tab synchronously (within this user gesture),
        // then awaits the fetch and fills it from the returned blob.
        return exportBatchPdf(
          'batch-chart-record',
          async () => {
            const result = await callBatchPrint();
            return result.data
              ? {
                  blob: new Blob([result.data as BlobPart], {
                    type: 'application/pdf',
                  }),
                }
              : null;
          },
          'chart record'
        );
      }
      return (async () => {
        try {
          const result = await callBatchPrint();
          if (result.error || !result.data) {
            console.error('Batch chart-record ZIP failed:', result.error);
            showError(
              'Export failed',
              'Could not generate the chart record. Please try again.'
            );
            return;
          }
          // The client-axios result IS the AxiosResponse (with `error`
          // augmented), so it carries the Content-Disposition filename.
          downloadResponseAsFile(result, 'chart_record.zip');
          const skippedNote =
            skippedUnprintable.length > 0
              ? ` ${skippedUnprintable.length} task${
                  skippedUnprintable.length === 1 ? '' : 's'
                } skipped (no printable document).`
              : '';
          showSuccess(
            'Export complete',
            `${printSet.length} task${printSet.length === 1 ? '' : 's'} exported.${skippedNote}`
          );
        } catch (err) {
          console.error('Batch chart-record ZIP error:', err);
          showError(
            'Export failed',
            'Could not generate the chart record. Please try again.'
          );
        }
      })();
    },
    [effectiveSelectedEpisodeId, exportBatchPdf, showError, showSuccess]
  );

  // Both print actions share this gate: if any selected task is not-started it
  // would render as a blank template, so disclose and let the user choose.
  const notStartedConfirm = useCallback(
    (items: TaskListResponse[]): ConfirmSpec | null => buildPrintConfirm(items),
    []
  );

  const batchActions = useMemo<SelectionAction<TaskListResponse>[]>(
    () => [
      {
        key: 'print-pdf',
        label: 'Print → Combined PDF',
        icon: FileText,
        confirm: notStartedConfirm,
        run: (items, choiceKey) => runBatchPrint(items, 'pdf', choiceKey),
      },
      {
        key: 'print-zip',
        label: 'Print → Download ZIP',
        icon: FileArchive,
        confirm: notStartedConfirm,
        run: (items, choiceKey) => runBatchPrint(items, 'zip', choiceKey),
      },
      {
        key: 'add-note',
        label: 'Add Note',
        icon: StickyNote,
        run: items => runBatchAddNote(items),
      },
      {
        key: 'delete',
        label: items =>
          `Delete (${partitionDeletable(items).deletable.length} eligible)`,
        icon: Trash2,
        iconClassName: 'text-destructive',
        destructive: true,
        confirm: items => {
          const { deletable, skippedLocked } = partitionDeletable(items);
          const skipped = skippedLocked.length;
          if (deletable.length === 0) {
            return {
              title: 'Nothing to delete',
              body: 'None of the selected tasks can be deleted \u2014 they are signed, completed, or locked.',
              choices: [{ key: 'ok', label: 'OK' }],
              cancelKeys: ['ok'],
            };
          }
          return {
            title: 'Delete tasks',
            body: `${deletable.length} task${
              deletable.length === 1 ? '' : 's'
            } will be deleted.${
              skipped > 0
                ? ` ${skipped} selected task${
                    skipped === 1 ? '' : 's'
                  } (signed, completed, or locked) will be skipped.`
                : ''
            }`,
            choices: [
              { key: 'cancel', label: 'Cancel', variant: 'outline' },
              { key: 'delete', label: 'Delete', variant: 'destructive' },
            ],
          };
        },
        run: items => runBatchDelete(items),
      },
    ],
    [notStartedConfirm, runBatchPrint, runBatchDelete, runBatchAddNote]
  );

  // A deleted row opens the same detail route a live one does, so the selected
  // task is resolved from whichever list holds it. The live list is consulted
  // first: if a task is in both, it was restored, and the editable view is the
  // truthful one.
  const selectedDeletedTask = useMemo(() => {
    if (!selectedVisitId) return null;
    if (tasks.some(t => t.id === selectedVisitId)) return null;
    return (
      deletedTasks.find(t => t.id === selectedVisitId) ??
      (linkedDeletedTask?.id === selectedVisitId ? linkedDeletedTask : null)
    );
  }, [tasks, deletedTasks, linkedDeletedTask, selectedVisitId]);
  const selectedTask = useMemo(
    () => tasks.find(t => t.id === selectedVisitId) ?? selectedDeletedTask,
    [tasks, selectedVisitId, selectedDeletedTask]
  );
  // Drives read-only mode on every detail panel below.
  const selectedTaskIsDeleted = selectedDeletedTask !== null;
  // Resolved against the episode's visits, so a selection made under another
  // episode stops being visit context the moment the header switches away from
  // it. `selectedVisitId` itself is left alone: switching back restores the
  // selection, and the Tasks tab's own detail panel reads it from `tasks`.
  const selectedVisitTask = useMemo(
    () => episodeVisitTasks.find(t => t.id === selectedVisitId) ?? null,
    [selectedVisitId, episodeVisitTasks]
  );
  // Wounds and Goals each filter the shared visit selection to types that have
  // that section (ST/transfer/death/supervisory/MSW/aide visits have no wound
  // section; transfer and death visits have no goals section). A selection
  // carried over from the other tab is ignored when it is not eligible here.
  // Goals still need a visit before the list can open. Wounds are patient-scoped:
  // the list stays readable and the in-card picker only enables writes.
  const woundVisitTasks = useMemo(
    () => episodeVisitTasks.filter(task => taskSupportsWounds(task)),
    [episodeVisitTasks]
  );
  const goalVisitTasks = useMemo(
    () => episodeVisitTasks.filter(task => taskShowsGoalsSection(task)),
    [episodeVisitTasks]
  );
  const woundVisitTask =
    selectedVisitTask && taskSupportsWounds(selectedVisitTask)
      ? selectedVisitTask
      : null;
  const goalVisitTask =
    selectedVisitTask && taskShowsGoalsSection(selectedVisitTask)
      ? selectedVisitTask
      : null;
  // Kinnser-migration flow: a migrated episode may have no visit that can open
  // goals, so Kinnser-enabled agencies get a button that saves goals straight
  // onto the selected episode (no visit). The backend enforces the same gate.
  const userAgency = useStore(state => state.user?.agency);
  const canEditPatientInfo = useStore(state =>
    state.hasPermission(PermissionSlug.PATIENT_INFO_EDIT)
  );
  // EMR-325. Viewing is the gate for the column; removing is a separate slug
  // the dialog checks for itself. Capture is deliberately not offered here —
  // this surface is read-only, unlike Hotbox.
  const showEvvColumn = useStore(state =>
    state.hasPermission(PermissionSlug.EVV_VIEW)
  );
  const canRemoveEvv = useStore(state =>
    state.hasPermission(PermissionSlug.EVV_REMOVE)
  );
  // Ignore a Patient Info save echo for a patient we have already navigated
  // away from. The tab unmounts while `loading` is true, but its in-flight
  // PUT can still resolve and call this after `getPatient(B)` has won.
  const handlePatientUpdated = useCallback((updated: PatientResponse) => {
    if (updated.id !== currentPatientIdRef.current) {
      return;
    }
    setPatient(updated);
  }, []);
  const canAddKinnserGoals = !!userAgency?.is_kinnser_enabled;
  const [isKinnserGoalsOpen, setIsKinnserGoalsOpen] = useState(false);
  // Bumped when the dialog saves a goal so the inline GoalsTab refetches.
  const [kinnserGoalsVersion, setKinnserGoalsVersion] = useState(0);
  // A 30-Day Summary communication renders full-page via its dedicated panel;
  // all other patient_communication types render full-page via
  // CommunicationDetailPanel (see the detail-panel switch in the return).
  const selectedTaskIsThirtyDaySummary = useMemo(
    () => !!selectedVisitId && isThirtyDaySummaryTask(selectedVisitId),
    [selectedVisitId, isThirtyDaySummaryTask]
  );
  const canRenderSelectedTaskDetail =
    activeTab === 'visits' && selectedVisitId && id && selectedTask;

  const handleTaskOpen = useCallback(
    (task: TaskListResponse) => {
      if (!id) return;
      setSelectedVisitId(task.id);
      setActiveTabState('visits');
      navigate(`/patients/${id}/visits/${task.id}`);
    },
    [id, navigate]
  );

  const handleVisitUpdated = useCallback(async () => {
    await Promise.all([loadEpisodes(), fetchTasks()]);
  }, [fetchTasks, loadEpisodes]);

  const handleMedicationOrderCreated = useCallback(
    async (order: OrderResponse) => {
      if (!id) return;
      setSelectedVisitId(order.id);
      setActiveTabState('visits');
      navigate(`/patients/${id}/visits/${order.id}`);
      showSuccess(
        'Medication Order Created',
        'Medication order created successfully.'
      );
      try {
        await fetchTasks();
      } catch (error) {
        console.warn(
          'Failed to refresh patient tasks after medication order',
          error
        );
      }
    },
    [fetchTasks, id, navigate, showSuccess]
  );

  const [medicationCount, setMedicationCount] = useState(0);
  const [allergyCount, setAllergyCount] = useState(0);
  const [documentCount, setDocumentCount] = useState(0);
  const [woundCount, setWoundCount] = useState(0);

  // Guards against out-of-order count refreshes: two mutations in quick
  // succession issue overlapping refreshCounts fetches, and without this a slower
  // earlier response could overwrite a newer count. Only the latest request wins.
  const countsRequestRef = useRef(0);
  const refreshCounts = useCallback(async () => {
    if (!id) return;
    const requestId = ++countsRequestRef.current;
    try {
      const [
        medsRes,
        allergiesRes,
        importedMedsRes,
        importedAllergiesRes,
        woundsRes,
        patientDocsRes,
        intakesRes,
      ] = await Promise.all([
        // The whole list, filtered for "active today" below. The server's
        // include_discontinued flag is presence-based, so it would drop a
        // medication with a planned future stop — one the patient is still
        // taking, and one the list underneath the badge still shows.
        getPatientMedications({
          path: { patient_id: id },
          query: { include_discontinued: true },
        }),
        getPatientAllergies({
          path: { patient_id: id },
        }),
        getImportedMedicationLines({
          path: { patient_id: id },
        }),
        getImportedAllergyProfile({
          path: { patient_id: id },
        }),
        getPatientWounds({
          path: { patient_id: id },
          query: { include_discontinued: false },
        }),
        getPatientDocuments({
          path: { patient_id: id },
          query: { include_task_documents: true },
        }),
        getIntakes({
          query: { patient_id: id },
        }),
      ]);

      const intakeDocumentCount =
        intakesRes?.data?.items?.reduce(
          (total, intake) =>
            total +
            (intake.documents?.filter(d => !d.task_type_tag).length ?? 0),
          0
        ) ?? 0;
      // A newer refresh superseded this one while it was in flight; drop it.
      if (requestId !== countsRequestRef.current) return;
      // Raw imported lines render in the med/allergy lists until someone
      // rules on them, so the badges count them too — a badge that disagrees
      // with the visible rows reads as a bug.
      setMedicationCount(
        countActiveMedications(medsRes?.data) +
          outstandingLines(importedMedsRes?.data?.lines).length
      );
      setAllergyCount(
        (allergiesRes?.data?.length ?? 0) +
          outstandingLines(importedAllergiesRes?.data?.lines).length
      );
      setWoundCount(woundsRes?.data?.length ?? 0);
      const patientDocCount =
        patientDocsRes?.data?.filter(d => d.document_type !== 'intake')
          .length ?? 0;
      setDocumentCount(patientDocCount + intakeDocumentCount);
    } catch {
      // Counts are non-critical
    }
  }, [id]);

  // Recompute counts on mount, patient change, and tab switch. Exposed as a
  // callback so a task-document upload/delete can refresh the Documents-tab badge
  // immediately, instead of it going stale until the tab is next opened (ROG-2987).
  useEffect(() => {
    void refreshCounts();
  }, [refreshCounts, activeTab]);

  // Refresh the Documents badge the moment a background document upload for this
  // patient completes — patientUploadTick is bumped by every patient- or
  // task-scoped upload. This is decoupled from the uploader's component
  // lifecycle, so it fires even though the paperclip popover closes on upload
  // and the Documents-tab +Add has no direct callback here.
  const patientUploadTick = useStore(
    state => state.patientUploadTick[id ?? ''] ?? 0
  );
  const prevPatientUploadTickRef = useRef(0);
  useEffect(() => {
    if (patientUploadTick !== prevPatientUploadTickRef.current) {
      prevPatientUploadTickRef.current = patientUploadTick;
      // Only the badge is PatientDetail's job here. The Documents list refetches
      // itself via its own tick effect when the tab is mounted; when the tab is
      // closed the ref is null anyway, so a refetch here would be redundant.
      void refreshCounts();
    }
  }, [patientUploadTick, refreshCounts]);

  // Visit-scoped Goals tab badge. Extracted into a hook so the request-id race
  // guard and reset-to-0 paths stay unit-testable; `refreshActiveGoalCount` lets a
  // goal mutation in GoalsTab refresh the badge immediately (ROG-3196).
  // Keyed off the visit the Goals tab actually renders, not the raw selection:
  // a visit from an episode we have switched away from is no longer goal
  // context, so its count must not stay on the badge over an empty tab.
  const { activeGoalCount, refreshActiveGoalCount } = useActiveGoalCount(
    goalVisitTask?.id ?? null,
    tasks,
    activeTab
  );

  useEffect(() => {
    // Refresh the badge when the episode changes and whenever task/visit data
    // refreshes (creating/editing/deleting visits changes authorization errors).
    void fetchAuthErrors();
  }, [fetchAuthErrors, tasksRefreshVersion]);

  if (loading) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <div className='flex justify-center mb-4'>
            <LoadingSpinner size='md' />
          </div>
          <p className='text-muted-foreground'>Loading patient details...</p>
        </div>
      </div>
    );
  }

  if (error || !patient) {
    return (
      <div className='flex items-center justify-center h-64'>
        <div className='text-center'>
          <AlertTriangle className='h-12 w-12 text-destructive mx-auto mb-4' />
          <h3 className='text-lg font-semibold mb-2'>Error Loading Patient</h3>
          <p className='text-muted-foreground mb-4'>
            {error || 'Patient not found'}
          </p>
          <Button onClick={() => navigate('/patients')} variant='outline'>
            <ArrowLeft className='h-4 w-4' />
            Back to Patients
          </Button>
        </div>
      </div>
    );
  }

  const tabItems: Array<{
    id: PatientTab;
    label: string;
    count?: number;
    isError?: boolean;
    errorMessages?: string[];
  }> = [
    { id: 'visits', label: 'Tasks', count: allTasks.length },
    { id: 'documents', label: 'Documents', count: documentCount },
    {
      id: 'medications',
      label: 'Medications',
      count: medicationCount,
    },
    { id: 'allergies', label: 'Allergies', count: allergyCount },
    { id: 'wounds', label: 'Wounds', count: woundCount },
    { id: 'goals', label: 'Goals', count: activeGoalCount },
    { id: 'insurance', label: 'Insurance' },
    {
      id: 'authorizations',
      label: 'Auths',
      count: authorizationErrorCount > 0 ? authorizationErrorCount : undefined,
      isError: true,
      errorMessages: authorizationErrors.map(e => e.message),
    },
    { id: 'events', label: 'Events' },
    { id: 'info', label: 'Patient Info' },
    { id: 'admission', label: 'Admission' },
  ];

  // Desktop underline tab. Mobile uses a compact section dropdown instead
  // of wrapping pills (EMR-1427), matching the OASIS section picker.
  const renderTabControl = (tab: (typeof tabItems)[number]) => {
    const isActive = activeTab === tab.id;
    const showErrorTooltip = Boolean(tab.isError && tab.errorMessages?.length);
    const tabButton = (
      <button
        key={tab.id}
        onClick={() => setActiveTab(tab.id)}
        className={`shrink-0 whitespace-nowrap px-3 py-2 text-sm font-medium border-b-2 transition-all ${
          isActive
            ? 'border-primary text-foreground'
            : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border hover:bg-muted/30'
        }`}
      >
        {tab.label}
        {tab.count != null && tab.count > 0 && (
          <Badge
            variant={tab.isError ? 'destructive' : 'secondary'}
            className='ml-1.5 text-[10px] px-1.5 py-0 h-4 min-w-4 inline-flex items-center justify-center'
          >
            {tab.count}
          </Badge>
        )}
      </button>
    );
    // Surface auth errors when hovering anywhere over the tab, not just the badge.
    return showErrorTooltip ? (
      <TooltipProvider key={tab.id}>
        <Tooltip>
          <TooltipTrigger asChild>{tabButton}</TooltipTrigger>
          <TooltipContent>
            <ul className='list-none space-y-0.5 text-xs'>
              {(tab.errorMessages ?? []).map((msg, i) => (
                <li key={i}>{msg}</li>
              ))}
            </ul>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    ) : (
      tabButton
    );
  };
  const addEpisodeControl =
    activeTab === 'visits' ? (
      <span className='inline-flex flex-col items-start gap-1'>
        <Button
          size='sm'
          variant='outline'
          disabled={!canAddEpisode}
          onClick={() => setAddEpisodeOpen(true)}
          title={canAddEpisode ? undefined : 'Complete intake first'}
        >
          <Plus className='h-4 w-4' />
          Add Episode
        </Button>
        {!canAddEpisode && (
          <span className='text-xs text-muted-foreground'>
            Complete intake first
          </span>
        )}
      </span>
    ) : null;

  // The detail view of a deleted task is read-only, so restore is the only
  // action it offers. It sits above the panel rather than inside each of the
  // four, so every task type gets the same control and the same refusal.
  const deletedTaskRestoreBanner =
    selectedTaskIsDeleted && selectedTask ? (
      <div className='flex flex-wrap items-center justify-between gap-2 border-b border-destructive/30 bg-destructive/5 px-3 py-2'>
        <span className='text-sm font-medium text-destructive'>
          This task is deleted.
        </span>
        <RestoreTaskButton
          task={selectedTask}
          taskLabel={getTaskDisplayName(selectedTask) || 'task'}
          restoring={restoringTaskId === selectedTask.id}
          onRestore={() =>
            void handleRestoreTask(selectedTask, { closeDetail: true })
          }
        />
      </div>
    ) : null;

  // From `md` up, detail panels own their own scrolling: an `h-full` root over
  // a `flex-1 overflow-auto` body. That only works when every parent has a
  // definite height, so bound this page to the viewport while a panel is open.
  // Without it the panel's scroll box just grows to content height, the page
  // scroller takes over, and nothing inside the panel can pin itself.
  //
  // Deliberately not applied on mobile. Bounding the page there pins the
  // patient and visit headers too, and at phone heights that chrome eats
  // roughly half the screen. Mobile keeps scrolling the page so both headers
  // scroll away, and the form's own pinned bar sticks to the page scroller
  // instead. The episode and tasks branch has no scroll box of its own and
  // grows into the page scroller at every width.
  return (
    <div
      className={`bg-background flex flex-col ${
        canRenderSelectedTaskDetail ? 'min-h-full md:h-full' : 'min-h-full'
      }`}
    >
      <PatientHeader
        patient={patient}
        insurances={patientInsurances}
        episodePatientInsuranceId={
          selectedEpisode?.patient_insurance_id ?? null
        }
        onEditEpisodeInsurance={() => {
          setIsEpisodeInsuranceEditorOpen(true);
        }}
        episodeId={selectedEpisode?.id ?? null}
        actions={
          <Button
            variant='outline'
            size='sm'
            onClick={() => setIsFaceSheetOpen(true)}
          >
            <ClipboardList className='h-4 w-4' />
            Summary
          </Button>
        }
      />

      <PatientFaceSheet
        patient={patient}
        episode={selectedEpisode ?? null}
        physicians={faceSheetPhysicians}
        insurances={patientInsurances}
        open={isFaceSheetOpen}
        onOpenChange={setIsFaceSheetOpen}
      />

      {/* Main Content */}
      {/* A deleted 30-Day Summary does not reach this branch:
        selectedTaskIsThirtyDaySummary is resolved from the live communications
        list, so a deleted one falls through to the generic communication panel,
        which is the one that reads deleted records and carries the restore
        banner. That panel recognises a summary and renders its print view, so
        the clinical content survives the detour; sending it here instead would
        mean making an editing panel that autosaves safe to open read-only. */}
      {canRenderSelectedTaskDetail && selectedTaskIsThirtyDaySummary ? (
        <div className='flex-1 flex flex-col min-h-0'>
          <ThirtyDaySummaryDetailPanel
            communicationId={selectedVisitId}
            patientId={id}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
              fetchTasks();
            }}
          />
        </div>
      ) : canRenderSelectedTaskDetail && selectedTask?.task_type === 'order' ? (
        <div className='flex-1 flex flex-col min-h-0'>
          {deletedTaskRestoreBanner}
          <OrderDetailPanel
            orderId={selectedVisitId}
            patientId={id}
            isDeleted={selectedTaskIsDeleted}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
              fetchTasks();
            }}
          />
        </div>
      ) : canRenderSelectedTaskDetail &&
        selectedTask?.task_type === 'patient_communication' ? (
        <div className='flex-1 flex flex-col min-h-0'>
          {deletedTaskRestoreBanner}
          <CommunicationDetailPanel
            // Remount per communication, same as the misc-task panel below.
            // This panel's useAutoSave sits at its top level, ABOVE the
            // `if (loading)` return, so swapping the id in place keeps the
            // hook (and its unsaved draft) alive across the switch: the new
            // record's data would be refused as an external value while the
            // previous one's edit is still dirty, leaving record A's notes on
            // screen under record B — and writing them there on the next edit.
            key={selectedVisitId}
            communicationId={selectedVisitId}
            patientId={id}
            isDeleted={selectedTaskIsDeleted}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
            }}
            onUpdated={() => void fetchTasks()}
          />
        </div>
      ) : canRenderSelectedTaskDetail &&
        selectedTask?.task_type === 'misc_task' ? (
        <div className='flex-1 flex flex-col min-h-0'>
          {deletedTaskRestoreBanner}
          <MiscTaskDetailPanel
            // Remount per task. The panel guards its own loads against a task
            // switch, but keying it means a new task cannot start from the
            // previous one's state at all.
            key={selectedVisitId}
            miscTaskId={selectedVisitId}
            patientId={id}
            isDeleted={selectedTaskIsDeleted}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
            }}
            onUpdated={() => void fetchTasks()}
          />
        </div>
      ) : canRenderSelectedTaskDetail ? (
        <div className='flex-1 flex flex-col min-h-0'>
          {deletedTaskRestoreBanner}
          <VisitDetailPanel
            visitId={selectedVisitId}
            patientId={id}
            patient={patient}
            isDeleted={selectedTaskIsDeleted}
            onBack={() => {
              setSelectedVisitId(null);
              navigate(`/patients/${id}`);
              fetchTasks();
            }}
            onVisitUpdated={handleVisitUpdated}
            physicians={getClinicPhysicians(patient?.clinic_id)}
          />
        </div>
      ) : (
        <div className='p-2 sm:p-5 max-lg:p-0'>
          <Card className='border-border/70 shadow-sm max-lg:rounded-none max-lg:border-0 max-lg:p-0 max-lg:shadow-none'>
            <CardHeader className='max-lg:px-3 max-lg:pt-2'>
              <div className='grid grid-cols-1 sm:grid-cols-[1fr_auto] items-start gap-3 max-lg:gap-2'>
                <CardDescription className='min-w-0 text-left'>
                  {selectedEpisode ? (
                    <span className='flex flex-col items-start gap-1'>
                      <span className='flex flex-wrap items-center gap-1.5'>
                        <span>
                          Episode: {formatDate(selectedEpisode.start_date)} –{' '}
                          {formatDate(selectedEpisode.end_date)}
                        </span>
                        <Button
                          type='button'
                          variant='ghost'
                          size='sm'
                          aria-label='Edit episode'
                          onClick={() => setIsEpisodeEditorOpen(true)}
                        >
                          <Pencil className='h-4 w-4' />
                        </Button>
                        {selectedEpisode.status && (
                          <Badge
                            variant={episodeStatusBadgeVariant(
                              selectedEpisode.status
                            )}
                            className='text-[10px] capitalize leading-normal'
                          >
                            {episodeStatusDisplayLabel(selectedEpisode.status)}
                          </Badge>
                        )}
                        {selectedEpisode.billed_externally && (
                          <ExternallyBilledBadge className='text-[10px] leading-normal' />
                        )}
                        {otherEpisodes.length > 0 && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant='linkMuted' size='sm'>
                                View {otherEpisodes.length} other episode
                                {otherEpisodes.length === 1 ? '' : 's'}
                                <ChevronDown />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align='start'
                              className='min-w-[260px]'
                            >
                              {otherEpisodes.map(ep => (
                                <DropdownMenuItem
                                  key={ep.id}
                                  onClick={() => setSelectedEpisodeId(ep.id)}
                                  className='flex items-center justify-between gap-3'
                                >
                                  <span>
                                    {formatDate(ep.start_date)} –{' '}
                                    {formatDate(ep.end_date)}
                                  </span>
                                  <span className='flex shrink-0 items-center gap-1'>
                                    {ep.status && (
                                      <Badge
                                        variant={episodeStatusBadgeVariant(
                                          ep.status
                                        )}
                                        className='text-[10px] capitalize leading-normal'
                                      >
                                        {episodeStatusDisplayLabel(ep.status)}
                                      </Badge>
                                    )}
                                    {ep.billed_externally && (
                                      <ExternallyBilledBadge className='text-[10px] leading-normal' />
                                    )}
                                  </span>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                        {addEpisodeControl}
                        <BillingManagerLink patientId={id!} />
                      </span>
                      {(selectedEpisode.assigned_clinician_name ||
                        selectedEpisode.case_manager_name ||
                        selectedEpisode.patient_insurance_name) && (
                        <span className='text-xs'>
                          {selectedEpisode.assigned_clinician_name && (
                            <>
                              Clinician:{' '}
                              <UnclaimedClinicianName
                                name={selectedEpisode.assigned_clinician_name}
                                unclaimed={
                                  selectedEpisode.assigned_clinician_unclaimed_import ===
                                  true
                                }
                                testId='episode-clinician-unclaimed'
                              />
                            </>
                          )}
                          {selectedEpisode.assigned_clinician_name &&
                            selectedEpisode.case_manager_name &&
                            ' · '}
                          {selectedEpisode.case_manager_name && (
                            <>
                              Case Manager:{' '}
                              <UnclaimedClinicianName
                                name={selectedEpisode.case_manager_name}
                                unclaimed={
                                  selectedEpisode.case_manager_unclaimed_import ===
                                  true
                                }
                                testId='episode-case-manager-unclaimed'
                              />
                            </>
                          )}
                          {(selectedEpisode.assigned_clinician_name ||
                            selectedEpisode.case_manager_name) &&
                            selectedEpisode.patient_insurance_name &&
                            ' · '}
                          {selectedEpisode.patient_insurance_name && (
                            <>
                              Insurance:{' '}
                              {selectedEpisode.patient_insurance_name}
                            </>
                          )}
                        </span>
                      )}
                    </span>
                  ) : (
                    <span className='flex flex-wrap items-start gap-2'>
                      <span>Episode tasks and assignments</span>
                      {addEpisodeControl}
                    </span>
                  )}
                </CardDescription>
                {(selectedEpisode || activeTab === 'visits') && (
                  <div className='flex flex-wrap justify-start sm:justify-end gap-2 max-lg:gap-1.5'>
                    {selectedEpisode && (
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        onClick={() => setIsEpisodeWorksheetOpen(true)}
                        disabled={!selectedEpisode}
                      >
                        Claim worksheet
                      </Button>
                    )}
                    {activeTab === 'visits' && addTaskTargetEpisode && (
                      // Every task in this dialog is a visit, so on a non-active
                      // episode the whole flow would be filled in and then
                      // rejected. Disabled rather than hidden so a missing
                      // button does not read as a bug, with the reason spelled
                      // out beside it: a disabled button has no pointer events,
                      // so its title never surfaces as a tooltip. Mirrors the
                      // Add Episode control above.
                      <span className='inline-flex flex-col items-start gap-1'>
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => setIsScheduleVisitsOpen(true)}
                          disabled={targetEpisodeBlocksVisits}
                        >
                          <CalendarPlus className='h-4 w-4' />
                          Schedule visits
                        </Button>
                        {targetEpisodeBlocksVisits && (
                          <span className='text-xs text-muted-foreground'>
                            Episode is not active
                          </span>
                        )}
                      </span>
                    )}
                    {activeTab === 'visits' && (
                      <Button size='sm' onClick={() => openAddTaskDialog()}>
                        <Plus className='h-4 w-4' />
                        Add Task
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </CardHeader>

            {/* Tab Navigation. Mobile uses a compact section dropdown (same
                idea as OASIS) so ten wrapping pills do not eat the viewport;
                desktop keeps the underline bar. Rendered one-at-a-time (not
                via CSS hidden) so only one set of tab controls is in the DOM. */}
            {isMobile ? (
              <div>
                <Select
                  value={activeTab}
                  onValueChange={value => {
                    if (isValidTab(value)) {
                      setActiveTab(value);
                    }
                  }}
                >
                  <SelectTrigger
                    selectSize='sm'
                    className='w-full rounded-none border-0 border-b border-border/60 px-3 shadow-none'
                    aria-label='Patient section'
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className='w-[var(--radix-select-trigger-width)]'>
                    {tabItems.map(tab => {
                      const countSuffix =
                        tab.count != null && tab.count > 0
                          ? ` (${tab.count})`
                          : '';
                      return (
                        <SelectItem key={tab.id} value={tab.id}>
                          {tab.isError && tab.errorMessages?.length
                            ? `⚠ ${tab.label}${countSuffix}`
                            : `${tab.label}${countSuffix}`}
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div
                data-testid='patient-chart-tabs'
                className='border-b border-border/60 px-6 flex items-center gap-1.5 overflow-x-auto'
              >
                {tabItems.map(tab => renderTabControl(tab))}
              </div>
            )}

            <CardContent className='pt-4 max-lg:px-3 max-lg:pt-2'>
              {/* ─── Tasks Tab ─── */}
              {activeTab === 'visits' && (
                <TasksTab
                  patientId={id!}
                  patientLabel={
                    patient
                      ? `${patient.first_name} ${patient.last_name}`
                      : undefined
                  }
                  effectiveSelectedEpisodeId={effectiveSelectedEpisodeId}
                  episodeStartDate={selectedEpisode?.start_date}
                  episodeEndDate={selectedEpisode?.end_date}
                  calendarTasks={calendarTasks}
                  assignableClinicians={assignableClinicians}
                  duplicateClinicianNames={duplicateClinicianNames}
                  lupaPeriods={lupaPeriods}
                  lupaLoading={lupaLoading}
                  authorizationBudget={episodeAuthBudget}
                  authorizationBudgetLoading={authBudgetLoading}
                  requiresAuthorization={episodeRequiresAuthorization}
                  unlinkedVisitCount={
                    authorizationErrors.find(
                      e => e.code === AUTHORIZATION_ERROR_CODE.UNLINKED_VISITS
                    )?.visit_count ?? 0
                  }
                  authorizationBudgetUnavailable={authBudgetUnavailable}
                  visitsLoading={visitsLoading}
                  visitsError={visitsError}
                  tasksForEpisode={tasksForEpisode}
                  displayedTasks={displayedTasks}
                  deletedTasks={deletedTasks}
                  deletedTaskById={deletedTaskById}
                  deletedTasksLoading={deletedTasksLoading}
                  deletedTasksError={deletedTasksError}
                  deletedTasksReady={deletedTasksReady}
                  taskView={taskView}
                  onTaskViewChange={setTaskView}
                  tableTaskTypeFilter={tableTaskTypeFilter}
                  onTableTaskTypeFilterChange={setTableTaskTypeFilterFromUser}
                  selectedTaskIds={selectedTaskIds}
                  selectedTasks={selectedTasks}
                  selectAllState={selectAllState}
                  atSelectionCap={atSelectionCap}
                  onToggleTaskSelected={toggleTaskSelected}
                  onToggleSelectAll={handleToggleSelectAll}
                  onClearTaskSelection={clearTaskSelection}
                  onRemoveTaskFromSelection={removeTaskFromSelection}
                  batchActions={batchActions}
                  getTaskDisplayName={getTaskDisplayName}
                  getTaskAssigneeLabel={getTaskAssigneeLabel}
                  getTaskAssigneeClinicians={getTaskAssigneeClinicians}
                  formatDuplicateClinicianOptionLabel={
                    formatDuplicateClinicianOptionLabel
                  }
                  isThirtyDaySummaryTask={isThirtyDaySummaryTask}
                  taskTargetDateBounds={taskTargetDateBounds}
                  updatingTaskId={updatingTaskId}
                  rejectedEdit={rejectedEdit}
                  showEvvColumn={showEvvColumn}
                  canViewSchedulingPointLoads={canViewSchedulingPointLoads}
                  restoringTaskId={restoringTaskId}
                  printTask={printTask}
                  printingTaskId={printingTaskId}
                  printDeletedTask={printDeletedTask}
                  printingDeletedTaskId={printingDeletedTaskId}
                  onTaskOpen={handleTaskOpen}
                  onAssigneeChange={handleAssigneeChange}
                  onTargetDateChange={handleTargetDateChange}
                  onCreateTaskFromDate={openAddTaskDialog}
                  onRequestDelete={setDeleteTask}
                  onRestoreTask={task => void handleRestoreTask(task)}
                  onOpenEvv={setEvvVisitId}
                  onDocumentsChanged={() => {
                    void fetchTasks({ silent: true });
                    void refreshCounts();
                  }}
                />
              )}

              {/* Documents Tab */}
              {activeTab === 'documents' && (
                <PatientDocumentsTab
                  // Keyed so the tab starts clean on patient navigation: it owns
                  // its own document fetches, signed URLs and scope choice, and
                  // none of them are patient-agnostic. Without this the previous
                  // patient's documents can paint under this patient's episode.
                  key={id}
                  ref={documentsRef}
                  patientId={id!}
                  patientLabel={
                    patient
                      ? `${patient.first_name} ${patient.last_name}`
                      : undefined
                  }
                  documentContextTasks={documentContextTasks}
                  episodeScope={documentsEpisodeScope}
                />
              )}

              {/* ─── Medications Tab ─── */}
              {activeTab === 'medications' && (
                <PatientMedications
                  patientId={id!}
                  episodeId={selectedEpisode?.id}
                  visitId={selectedVisitTask?.id}
                  visitStatus={selectedVisitTask?.status}
                  episodeClosed={selectedVisitTask?.episode_closed}
                  visitSelector={
                    <div className='flex w-full flex-wrap items-center gap-2 sm:w-auto'>
                      <VisitTaskPicker
                        items={[...episodeVisitTasks].sort(
                          (a, b) =>
                            (parseISOToLocalDate(
                              b.task_date ?? b.scheduled_date
                            )?.getTime() ?? 0) -
                            (parseISOToLocalDate(
                              a.task_date ?? a.scheduled_date
                            )?.getTime() ?? 0)
                        )}
                        valueId={selectedVisitTask?.id ?? null}
                        onValueIdChange={id => setSelectedVisitId(id)}
                        placeholder='Select visit for MAR'
                        selectTriggerClassName='w-full sm:w-[300px]'
                        renderItemLabel={task => (
                          <>
                            {task.display_name} -{' '}
                            {formatDate(task.task_date ?? task.scheduled_date)}
                          </>
                        )}
                        emptyHelper={
                          <span className='text-xs text-muted-foreground'>
                            Select a visit to record administrations
                          </span>
                        }
                      />
                    </div>
                  }
                  onMedicationOrderCreated={handleMedicationOrderCreated}
                  onDocumentCreated={() =>
                    documentsRef.current?.refreshDocuments()
                  }
                  onMedicationsChanged={() => void refreshCounts()}
                />
              )}

              {/* Allergies Tab */}
              {activeTab === 'allergies' && (
                <PatientAllergies
                  patient={patient}
                  onAllergiesChanged={() => void refreshCounts()}
                  onPatientChanged={() => void refreshPatient()}
                />
              )}

              {/* Goals still require a visit before the list can open. Wounds
                  keep their picker inside the card, next to the actions it
                  enables, the same way Medications does. */}
              {activeTab === 'goals' && (
                <div className='mb-4 mt-1'>
                  {/* With no eligible visit there is nothing to pick, so the tab
                      body carries the single explanatory message instead. The
                      external-EMR goals button still renders there: the
                      migration flow exists exactly because such episodes can
                      have no goal-eligible visit. */}
                  {canAddKinnserGoals &&
                    effectiveSelectedEpisodeId &&
                    goalVisitTasks.length === 0 && (
                      <Button
                        size='sm'
                        variant='outline'
                        onClick={() => setIsKinnserGoalsOpen(true)}
                        className='flex items-center gap-1'
                      >
                        <Plus className='h-4 w-4' />
                        Add Goals from External EMR
                      </Button>
                    )}
                  {goalVisitTasks.length > 0 && (
                    <>
                      <div className='flex flex-wrap items-center gap-2'>
                        <VisitTaskPicker
                          items={[...goalVisitTasks].sort(
                            (a, b) =>
                              new Date(
                                b.task_date ?? b.scheduled_date
                              ).getTime() -
                              new Date(
                                a.task_date ?? a.scheduled_date
                              ).getTime()
                          )}
                          valueId={goalVisitTask?.id ?? null}
                          onValueIdChange={id => setSelectedVisitId(id)}
                          placeholder='Select visit'
                          selectTriggerClassName={`w-full sm:w-[360px] ${
                            !goalVisitTask
                              ? 'border-destructive/60 text-destructive'
                              : ''
                          }`}
                          renderItemLabel={task => (
                            <>
                              {task.display_name} -{' '}
                              {formatDate(
                                task.task_date ?? task.scheduled_date
                              )}
                            </>
                          )}
                        />
                        {canAddKinnserGoals && effectiveSelectedEpisodeId && (
                          <Button
                            size='sm'
                            variant='outline'
                            onClick={() => setIsKinnserGoalsOpen(true)}
                            className='flex items-center gap-1'
                          >
                            <Plus className='h-4 w-4' />
                            Add Goals from External EMR
                          </Button>
                        )}
                      </div>
                      {!goalVisitTask && (
                        <p className='mt-2 inline-flex items-center gap-1.5 text-xs text-destructive'>
                          <AlertCircle className='h-3.5 w-3.5' />
                          Select a visit to enable goals.
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Wounds Tab */}
              {activeTab === 'wounds' && (
                <PatientWounds
                  patientId={id!}
                  visitId={woundVisitTask?.id}
                  visitStatus={woundVisitTask?.status}
                  episodeClosed={woundVisitTask?.episode_closed}
                  // Chart tab only — visit notes stay a flat table so Live
                  // Scrubber can jump to discontinued rows (EMR-931).
                  splitByStatus
                  // Unfiltered on purpose: this is the lookup the assessments
                  // drawer uses to name the visit each assessment was recorded
                  // on, including visits in an earlier episode and visits whose
                  // type no longer has a wound section. Wounds are patient
                  // history, so the names have to outlive the episode on
                  // screen. The picker above is what gets filtered.
                  visits={visitTasks}
                  onWoundsChanged={() => void refreshCounts()}
                  // Wounds are patient-scoped history, so the list stays
                  // readable with no visit selected. This banner is only for
                  // the case where no visit type can ever serve as context.
                  noVisitContextReason={
                    woundVisitTasks.length === 0
                      ? `${
                          selectedEpisode
                            ? 'No visits in this episode have a wounds section.'
                            : 'This patient has no episode yet, so there is no visit to assess a wound on.'
                        } Existing wounds can be corrected or removed here, but not added or assessed.`
                      : undefined
                  }
                  visitSelector={
                    woundVisitTasks.length > 0 ? (
                      <div className='flex min-w-0 flex-wrap items-center gap-2'>
                        <VisitTaskPicker
                          items={[...woundVisitTasks].sort(
                            (a, b) =>
                              new Date(
                                b.task_date ?? b.scheduled_date
                              ).getTime() -
                              new Date(
                                a.task_date ?? a.scheduled_date
                              ).getTime()
                          )}
                          valueId={woundVisitTask?.id ?? null}
                          onValueIdChange={id => setSelectedVisitId(id)}
                          placeholder='Select visit'
                          selectTriggerClassName='w-full sm:w-[300px]'
                          renderItemLabel={task => (
                            <>
                              {task.display_name} -{' '}
                              {formatDate(
                                task.task_date ?? task.scheduled_date
                              )}
                            </>
                          )}
                          emptyHelper={
                            <span className='text-xs text-muted-foreground'>
                              Select a visit to enable wound actions
                            </span>
                          }
                        />
                      </div>
                    ) : undefined
                  }
                />
              )}

              {/* Goals Tab */}
              {activeTab === 'goals' &&
                (goalVisitTask ? (
                  <div className='h-[calc(100vh-10rem)]'>
                    <GoalsTab
                      // Remount when the Kinnser dialog saves a goal so the
                      // list refetches and shows it.
                      key={`${goalVisitTask.id}-${kinnserGoalsVersion}`}
                      open={true}
                      onClose={() => {}}
                      patientFirstName={patient.first_name}
                      patientLastName={patient.last_name}
                      visit={goalVisitTask}
                      inline={true}
                      capabilities={visitGoalCapabilities(
                        goalVisitTask.visit_type as VisitTypeId
                      )}
                      onGoalsChanged={() => void refreshActiveGoalCount()}
                    />
                  </div>
                ) : (
                  <div className='flex flex-col items-center justify-center py-12 text-muted-foreground'>
                    <Target className='h-12 w-12 mb-4 opacity-30' />
                    <p className='text-base font-medium'>
                      {goalVisitTasks.length > 0
                        ? 'Select a visit to get started.'
                        : selectedEpisode
                          ? 'No visits in this episode have a goals section.'
                          : 'This patient has no episode yet, so there is no visit to set a goal on.'}
                    </p>
                  </div>
                ))}
              {canAddKinnserGoals && selectedEpisode && (
                <AddExternalEmrGoalsDialog
                  open={isKinnserGoalsOpen}
                  onClose={() => setIsKinnserGoalsOpen(false)}
                  episodeId={selectedEpisode.id}
                  episodeStartDate={selectedEpisode.start_date}
                  episodeEndDate={selectedEpisode.end_date}
                  onGoalAdded={() => {
                    void refreshActiveGoalCount();
                    setKinnserGoalsVersion(v => v + 1);
                  }}
                />
              )}

              {/* ─── Authorizations Tab ─── */}
              {activeTab === 'authorizations' && (
                <AuthorizationsTab
                  patientId={id!}
                  episodeId={effectiveSelectedEpisodeId}
                  episodeStartDate={selectedEpisode?.start_date}
                  episodeEndDate={selectedEpisode?.end_date}
                  defaultInsuranceId={selectedEpisode?.patient_insurance_id}
                  onErrorsChanged={fetchAuthErrors}
                />
              )}

              {/* ─── Events Tab ─── */}
              {activeTab === 'events' && (
                <EventsTab
                  patientId={id!}
                  episodeId={effectiveSelectedEpisodeId}
                  episodeStartDate={selectedEpisode?.start_date}
                  episodeEndDate={selectedEpisode?.end_date}
                  episodes={episodes}
                  clinicians={assignableClinicians}
                  physicians={getClinicPhysicians(patient?.clinic_id)}
                />
              )}

              {/* ─── Insurance Tab ─── */}
              {activeTab === 'insurance' && (
                <InsuranceTab
                  patientId={patient.id}
                  onAddInsurance={() => {
                    setEditingInsuranceId(null);
                    setInsurancePopupOpen(true);
                  }}
                  onEditInsurance={insuranceId => {
                    setEditingInsuranceId(insuranceId);
                    setInsurancePopupOpen(true);
                  }}
                  insuranceRefreshTrigger={insuranceRefreshTrigger}
                  onInsurancesChanged={() => void refreshPatientInsurances()}
                />
              )}

              {/* ─── Patient Info Tab ─── */}
              {activeTab === 'info' && (
                <PatientInfoTab
                  patient={patient}
                  canEditPatientInfo={canEditPatientInfo}
                  onPatientUpdated={handlePatientUpdated}
                />
              )}

              {/* ─── Admission Tab ─── */}
              {activeTab === 'admission' && (
                <AdmissionTab
                  admissionId={selectedEpisode?.admission_id}
                  clinicId={patient?.clinic_id}
                />
              )}
            </CardContent>
          </Card>
          <AddEpisodeModal
            open={addEpisodeOpen}
            onClose={() => setAddEpisodeOpen(false)}
            patientId={id!}
            clinicId={patient.clinic_id ?? ''}
            previousEpisode={previousEpisode}
            existingEpisodes={episodes}
            onCreated={newEpisode => {
              setEpisodes(prev => [
                newEpisode,
                ...prev.filter(episode => episode.id !== newEpisode.id),
              ]);
              setSelectedEpisodeId(newEpisode.id);
              void loadEpisodes();
            }}
          />
          <PatientInsuranceFormDialog
            open={insurancePopupOpen}
            onClose={() => {
              setInsurancePopupOpen(false);
              setEditingInsuranceId(null);
              setInsurancesLoading(true);
              void listPatientInsurances({
                path: { patient_id: id! },
              })
                .then(res => {
                  if (res.data) {
                    setPatientInsurances(res.data);
                    setInsuranceRefreshTrigger(prev => prev + 1);
                  }
                })
                .catch(err => {
                  console.error('Failed to load patient insurances:', err);
                })
                .finally(() => {
                  setInsurancesLoading(false);
                });
            }}
            patientId={id!}
            clinicId={patient.clinic_id}
            patient={patient}
            editingInsuranceId={editingInsuranceId}
          />
        </div>
      )}

      {/* Schedule Visits Dialog (bulk create across multiple dates) */}
      {addTaskTargetEpisode && (
        <ScheduleVisitsDialog
          open={isScheduleVisitsOpen}
          onOpenChange={setIsScheduleVisitsOpen}
          patientId={id!}
          episodeId={addTaskTargetEpisode.id}
          episodeStartDate={addTaskTargetEpisode.start_date}
          episodeEndDate={addTaskTargetEpisode.end_date}
          visitTypeOptions={visitTypeOptions}
          clinicians={assignableClinicians}
          duplicateClinicianNames={duplicateClinicianNames}
          onCreated={() => {
            // Bulk scheduling is the single largest mover of a clinician's
            // week — it can add a dozen visits at once — so it is the last
            // place a stale cached load should survive.
            void invalidateSchedulingPointLoads();
            return fetchTasks();
          }}
          requiresAuthorization={episodeRequiresAuthorization}
        />
      )}

      <AddTaskDialog
        open={isAddDialogOpen}
        onOpenChange={open => {
          if (open) {
            openAddTaskDialog();
            return;
          }
          setIsAddDialogOpen(false);
        }}
        initialDate={addTaskInitialDate}
        initialFilter={tableTaskTypeFilter}
        patientId={id!}
        addTaskTargetEpisode={addTaskTargetEpisode}
        episodes={episodes}
        visitTypeOptions={visitTypeOptions}
        getClinicAssignableClinicians={getClinicAssignableClinicians}
        getClinicPhysicians={getClinicPhysicians}
        formatDuplicateClinicianOptionLabel={
          formatDuplicateClinicianOptionLabel
        }
        episodeRequiresAuthorization={episodeRequiresAuthorization}
        effectiveSelectedEpisodeId={effectiveSelectedEpisodeId}
        tasksRefreshVersion={tasksRefreshVersion}
        canViewSchedulingPointLoads={canViewSchedulingPointLoads}
        patientClinicId={patientClinicId}
        onCreated={() => fetchTasks()}
        onCommunicationCreated={communication =>
          setCommunications(prev => [...prev, communication])
        }
        invalidateSchedulingPointLoads={() => {
          void invalidateSchedulingPointLoads();
        }}
        showSuccess={showSuccess}
        showError={showError}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!deleteTask}
        onOpenChange={open => !open && setDeleteTask(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Task</DialogTitle>
          </DialogHeader>
          <p className='text-sm text-muted-foreground'>
            Are you sure you want to delete this task? This action cannot be
            undone.
          </p>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setDeleteTask(null)}
              disabled={isDeletingTask}
            >
              Cancel
            </Button>
            <Button
              variant='destructive'
              onClick={handleDeleteTask}
              disabled={isDeletingTask}
            >
              {isDeletingTask ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Episode Dialog */}
      <Dialog open={isEpisodeEditorOpen} onOpenChange={setIsEpisodeEditorOpen}>
        <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-lg'>
          <DialogHeader>
            <DialogTitle>Edit Episode</DialogTitle>
            <DialogDescription className='sr-only'>
              Edit episode information.
            </DialogDescription>
          </DialogHeader>
          {selectedEpisode && (
            <EpisodeEditor
              episodeId={selectedEpisode.id}
              onCancel={() => setIsEpisodeEditorOpen(false)}
              onSaved={() => {
                setIsEpisodeEditorOpen(false);
                void loadEpisodes();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      {episodeDateSync && (
        <EpisodeDateSyncDialog
          open
          boundary={episodeDateSync.boundary}
          episodeId={episodeDateSync.episodeId}
          visitId={episodeDateSync.visitId}
          newDate={episodeDateSync.newDate}
          currentBoundaryDate={episodeDateSync.currentBoundaryDate}
          episodeStartDate={episodeDateSync.episodeStartDate}
          episodeEndDate={episodeDateSync.episodeEndDate}
          visitLabel={episodeDateSync.visitLabel}
          onClose={() => setEpisodeDateSync(null)}
          onCompleted={() => {
            void fetchTasks({ silent: true });
            void loadEpisodes();
          }}
          onUpdateVisitOnly={() => {
            // Move only the visit, leaving the episode dates untouched. The
            // standard reschedule path enforces that the date stays inside the
            // episode window (the dialog only offers this when it does).
            const { visitId, newDate } = episodeDateSync;
            setEpisodeDateSync(null);
            void handleTaskUpdate(
              visitId,
              { scheduled_date: newDate },
              'Target date updated successfully.'
            );
          }}
        />
      )}

      <EpisodeInsuranceSelector
        open={isEpisodeInsuranceEditorOpen}
        onClose={() => setIsEpisodeInsuranceEditorOpen(false)}
        onConfirm={handleUpdateEpisodeInsurance}
        patientInsurances={patientInsurances}
        currentInsuranceId={selectedEpisode?.patient_insurance_id}
        insurancesLoading={insurancesLoading}
        patientId={id}
      />

      {/* Mounted here, outside the tab and task-view conditionals, so a tab
        change while it is open cannot unmount it and strand its scroll lock. */}
      <BatchNotesDialog
        open={batchNotesOpen}
        onOpenChange={setBatchNotesOpen}
        tasks={batchNotesTasks}
        onSuccess={count => {
          showSuccess(
            'Note added',
            `Note added to ${count} task${count === 1 ? '' : 's'}.`
          );
          clearTaskSelection();
          void fetchTasks({ silent: true });
        }}
      />

      {/* EMR-325: EVV readings for the row whose indicator was clicked.
        Mounted only while open so each visit's readings are fetched fresh,
        matching how Hotbox drives this dialog. */}
      {evvVisitId && (
        <EvvReadingsDialog
          open={evvVisitId !== null}
          onOpenChange={open => {
            if (!open) setEvvVisitId(null);
          }}
          visitId={evvVisitId}
          canRemove={canRemoveEvv}
          onRemoved={() => void fetchTasks({ silent: true })}
        />
      )}

      {/* Episode Worksheet Editor */}
      {isEpisodeWorksheetOpen && selectedEpisode && (
        <EpisodeWorksheetEditor
          episodeId={selectedEpisode.id}
          onClose={() => setIsEpisodeWorksheetOpen(false)}
        />
      )}
    </div>
  );
};

export default PatientDetail;
